import React, { FunctionComponent, useCallback, useState } from 'react';

import _ from 'lodash';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { useProfile } from 'auth/Auth';
import { Overlay } from 'core/overlay/Overlay';
import { AsyncSelect } from 'core/select/Select';
import { ButtonVariant, Size } from 'core/util/Bootstrap';
import { LocationTag } from 'social/publisher/Publisher.types';
import { usePublisher } from 'social/publisher/PublisherContext';
import { fetchTwitterLocationTag } from 'social/publisher/PublisherFetchers';

import 'social/publisher/location-tag-selector/LocationTagSelector.scss';

interface Props {
    onClose: any;
    onActive: any;
}

type Option = {
    value: string;
    label: string;
    id: string;
    name: string;
    fullName: string;
    country: string;
    countryCode: string;
    placeType: string;
};

const OVERLAY_AREA = 'social-publisher-post-target';
const buildLocationTagOption = (locationTag: LocationTag): Option => {
    const { id, name, fullName, country, countryCode, placeType } = locationTag;

    return {
        value: id,
        label: fullName,
        id,
        name,
        fullName,
        country,
        countryCode,
        placeType,
    };
};
const getLocationTagFromOption = (option: Option): LocationTag => {
    const { id, name, fullName, country, countryCode, placeType } = option;

    return {
        id,
        name,
        fullName,
        country,
        countryCode,
        placeType,
    };
};

export const LocationTagSelector: FunctionComponent<Props> = ({ onClose, onActive }) => {
    const { t } = useTranslation(['social/publisher']);
    const [{ profile }] = useProfile();
    const [{ selectedSources, locationTag }, dispatch] = usePublisher();
    const tenantId = profile?.tenant?.id;

    let selectedTagOption: Option | null = null;

    if (locationTag) {
        selectedTagOption = buildLocationTagOption(locationTag);
    }

    const [selectedOption, setSelectedOption] = useState<Option | null>(selectedTagOption);

    const handleLocationTagChange = (selectedOption: Option | null): void => {
        setSelectedOption(selectedOption);
    };

    const handleSave = (): void => {
        let newLocationTag: LocationTag | null = null;

        if (selectedOption) {
            newLocationTag = getLocationTagFromOption(selectedOption);
        }

        dispatch({ type: 'set-location-tag', payload: { value: newLocationTag } });

        if (newLocationTag) {
            onActive(true);
        } else {
            onActive(false);
        }
        onClose();
    };

    const handleCancel = (): void => {
        onClose();
    };

    const loadLocationTagOptions = useCallback(
        (input: string, callback: (...args: any) => any): void => {
            const query = input.trim() ? input.trim() : 'california';

            if (query && query.length > 1) {
                trackPromise(fetchTwitterLocationTag(tenantId, query), OVERLAY_AREA).then((locationTags) => {
                    let options: Option[] = [];

                    if (!_.isEmpty(locationTags)) {
                        options = locationTags.map(buildLocationTagOption);
                    }
                    callback(options);
                });
            }
        },
        [tenantId],
    );

    return (
        <div className="r4e-social-location-tag-selector">
            <Overlay area={OVERLAY_AREA} />
            <div className="location-tag-selector-header">
                <div className="location-tag-selector-header-text">{t('TWITTER_LOCATION_TAGGING')}</div>
            </div>
            <div className="location-tag-selector-body">
                {selectedSources.TWITTER ? (
                    <>
                        <div className="location-tag-select-label">{t('LOCATION_SEARCH')}</div>
                        <AsyncSelect
                            name="locationTag"
                            classNamePrefix="location-tag-select"
                            className="location-tag-select"
                            loadOptions={_.debounce(loadLocationTagOptions, 300)}
                            value={selectedOption}
                            onChange={handleLocationTagChange}
                            placeholder={t('SEARCH')}
                            cacheOptions
                            defaultOptions
                        />
                    </>
                ) : (
                    <>{t('SELECT_TWITTER_TO_SEE_LOCATION_TAGS')}</>
                )}
            </div>
            <div className="location-tag-selector-footer">
                {selectedSources.TWITTER && (
                    <>
                        <Button
                            variant={ButtonVariant.Secondary}
                            size={Size.Small}
                            className="cancel-btn"
                            onClick={handleCancel}
                        >
                            {t('CANCEL')}
                        </Button>
                        <Button
                            variant={ButtonVariant.Primary}
                            size={Size.Small}
                            className="save-btn"
                            onClick={handleSave}
                        >
                            {t('SAVE')}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};
